import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import Vehicle, { FindVehicleResult } from "../passapp-sdk/vehicle";
import { Vehicle as VehicleModel } from "../passapp-sdk/models/Vehicle";
import { useStore } from "./useStore";

/** Mismo mínimo que valida el backend ("Ingrese al menos 3 caracteres de la patente."): se corta
 * en el cliente para no gastar una llamada que sabemos que va a fallar. */
export const MIN_PLATE_SEARCH_LENGTH = 3;

const vehicleApi = new Vehicle();

const normalizePlate = (plate: string): string => plate.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

type SelectVehicleResult =
  | { success: true; vehicle: VehicleModel }
  | { success: false; error: string };

/** Búsqueda de vehículos por patente (`findVehicles`) + confirmación de selección
 * (`selectVehicle`), reemplazo de la precarga vía `getVehicles`. `personId` es la persona
 * identificada en pantalla: sin ella no hay contra qué buscar ni seleccionar. `searchTerm` lo
 * controla el caller (normalmente un campo del formulario, para que sobreviva a los re-renders). */
/** `refreshKey` permite volver a pedir el mismo texto después de crear o editar una fila. Sin él,
 * React no reejecutaría el efecto porque la patente no cambió y el dropdown conservaría su etiqueta
 * anterior hasta que el guardia modificara el buscador. */
const useVehicleSearch = (
  personId: string | null | undefined,
  searchTerm: string,
  refreshKey = 0
) => {
  const { authStore } = useStore();
  const [results, setResults] = useState<FindVehicleResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selecting, setSelecting] = useState(false);
  // Cancelar un debounce no cancela una request que ya salió. Sin este contador, una búsqueda
  // anterior puede terminar después del refresh posterior al update y volver a pintar su marca
  // vieja (el caso de una patente que parece "cacheada").
  const latestSearchRequestRef = useRef(0);

  const runSearch = useCallback(
    debounce(async (plate: string, token: string, user: string, requestId: number) => {
      try {
        const response = await vehicleApi.findVehicles(token, user, plate);
        if (requestId !== latestSearchRequestRef.current) return;
        setResults(response?.data ?? []);
      } catch (err) {
        if (requestId !== latestSearchRequestRef.current) return;
        console.warn("Error buscando vehículos:", err);
        const message = typeof err === "string" ? err : (err as any)?.message || (err as any)?.msg;
        setSearchError(typeof message === "string" ? message : "No se pudo buscar el vehículo.");
        setResults([]);
      } finally {
        if (requestId === latestSearchRequestRef.current) setLoading(false);
      }
    }, 350),
    []
  );

  useEffect(() => {
    const plate = (searchTerm || "").trim();
    const requestId = ++latestSearchRequestRef.current;
    setSearchError(null);
    if (!authStore.token || !personId || plate.length < MIN_PLATE_SEARCH_LENGTH) {
      runSearch.cancel();
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    runSearch(plate, authStore.token, personId, requestId);
    return () => runSearch.cancel();
  }, [searchTerm, authStore.token, personId, runSearch, refreshKey]);

  /** Se llama en cada selección, no sólo la primera: trae al complejo lo que el usuario haya
   * actualizado en su app y habilita leer sus fotos. El `id` que devuelve puede ser distinto del
   * buscado — el caller tiene que usar ese, no el de `results`. */
  const selectVehicle = useCallback(
    async (vehicleId: string): Promise<SelectVehicleResult> => {
      if (!authStore.token || !personId) {
        return { success: false, error: "Sesión inválida." };
      }
      setSelecting(true);
      try {
        const response = await vehicleApi.selectVehicle(authStore.token, personId, vehicleId);
        return { success: true, vehicle: response.data };
      } catch (err) {
        console.warn("Error seleccionando vehículo:", err);
        const message = typeof err === "string" ? err : (err as any)?.message || (err as any)?.msg;
        return {
          success: false,
          error: typeof message === "string" ? message : "Vehículo no encontrado.",
        };
      } finally {
        setSelecting(false);
      }
    },
    [authStore.token, personId]
  );

  /** Búsqueda puntual (no debounced, no toca `results`) para el caso del prefill de autorización de
   * transporte: si el chofer ya tiene registrada la patente que impone la autorización, la
   * encontramos y la seleccionamos sin que el guardia tenga que tipear nada. */
  const findExact = useCallback(
    async (plate: string): Promise<FindVehicleResult | null> => {
      const trimmed = plate.trim();
      if (!authStore.token || !personId || trimmed.length < MIN_PLATE_SEARCH_LENGTH) return null;
      try {
        const response = await vehicleApi.findVehicles(authStore.token, personId, trimmed);
        const target = normalizePlate(trimmed);
        return (response?.data ?? []).find((v) => normalizePlate(v.plate) === target) ?? null;
      } catch (err) {
        console.warn("Error buscando patente exacta:", err);
        return null;
      }
    },
    [authStore.token, personId]
  );

  return { results, loading, searchError, selecting, selectVehicle, findExact };
};

export default useVehicleSearch;
